import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { Route, Switch, useParams } from "react-router";
import { OBJECT_TYPE, WORKFLOW_STATUS } from "common/constants";
import { buildLink, getPath, INFO_PANEL_TAB_NAME } from "common/routing";
import {
  FILTER_OPERATORS,
  QueryDescription,
  TABLE_NAMES,
} from "common/utils/queryBuilder";
import { pluralize } from "common/utils/strings";
import {
  GetQueryTableDataQueryVariables,
  useGetQueryTableDataLazyQuery,
  useGetSavedViewsQuery,
  useGetTableConfigQuery,
} from "../../generated/graphql";
import {
  buildLocalTableInfo,
  useLocalTableDisplayConfig,
} from "../../hooks/useTableDisplayConfig";
import { AuthContext } from "../Authorization/AuthContext";
import { ACTION_COLUMN_DEF_CONSTANTS, ActionCell } from "../Common/ActionCell";
import { Layout } from "../Common/FullWidthLayout";
import { FullWidthTable } from "../Common/FullWidthTable/FullWidthTable";
import { Attribute } from "../Common/FullWidthTable/types";
import {
  fieldId,
  initializeColumns,
  mergeFieldGroups,
  MinimalColumnDef,
  sanityCheckLocalColumnOrder,
} from "../Common/FullWidthTable/utils";
import LinkCell from "../Common/LinkCell";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../Common/Tables/hooks";
import { buildColumnDefinitions } from "../Common/Tables/utils";
import ExportDataButton from "../Exports/ExportDataButton";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { CUSTOM_PROPERTY_LINK_CELLS } from "../Property/PropertiesTable";
import { WorkflowStatusTag } from "./WorkflowStatusTag";

type WorkflowRowData = {
  "Workflows.id": string;
  "Workflows.objectId": string;
  "Workflows.objectType": string;
  "Workflows.canShare": boolean;
  "WorkflowDefinitions.name": string;
  "Workflows.status"?: "Not started" | "In progress" | "Complete";
  "Properties.latitude": number;
  "Properties.longitude": number;
  "Properties.streetAddress": string;
};

export const WORKFLOWS_TABLE_REQUIRED_FIELDS = [
  { table: TABLE_NAMES.WORKFLOWS, name: "id" },
  { table: TABLE_NAMES.WORKFLOWS, name: "objectId" },
  { table: TABLE_NAMES.WORKFLOWS, name: "objectType" },
  { table: TABLE_NAMES.WORKFLOWS, name: "canShare" },
  { table: TABLE_NAMES.WORKFLOW_DEFINITIONS, name: "name" },
  { table: TABLE_NAMES.PROPERTIES, name: "id" },
  { table: TABLE_NAMES.PROPERTIES, name: "latitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "longitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "streetAddress" },
] as const;

type RowWithRequiredFields = {
  original: WorkflowRowData;
};

const makeCustomCellRenderers = ({}: { refetchTable: () => void }) => ({
  "WorkflowDefinitions.name": {
    cell: ({ row }: { row: RowWithRequiredFields }) => {
      const workflowName = row.original["WorkflowDefinitions.name"];
      const workflowId = row.original["Workflows.id"];

      const url = buildLink("workflow", { workflowId });

      return <LinkCell href={url} target="_blank" label={workflowName} />;
    },
  },
  "Workflows.status": {
    cell: ({ row }: { row: RowWithRequiredFields }) => {
      const status = row.original["Workflows.status"];
      return status ?
          <WorkflowStatusTag status={status as WORKFLOW_STATUS} />
        : null;
    },
  },
  ...CUSTOM_PROPERTY_LINK_CELLS,
});

const WorkflowActions = ({ rowData }: { rowData: WorkflowRowData }) => {
  const { account } = useContext(AuthContext);
  const id = rowData["Workflows.id"];
  const objectId = rowData["Workflows.objectId"];
  const objectType = rowData["Workflows.objectType"];
  const latitude = rowData["Properties.latitude"];
  const longitude = rowData["Properties.longitude"];
  const streetAddress = rowData["Properties.streetAddress"];

  const isPropertyWorkflow = objectType === OBJECT_TYPE.PROPERTY;
  const hasLocationData = latitude && longitude;

  const actions: Array<ActionsProps> = [
    {
      label: "View",
      href: buildLink("workflow", { workflowId: id }),
      target: "_blank",
    },
  ];

  if (isPropertyWorkflow && hasLocationData) {
    actions.push({
      label: "View on map",
      href: buildLink(
        "map",
        { accountId: account?.id! },
        {
          lat: latitude.toString(),
          lng: longitude.toString(),
          address: streetAddress ?? "",
          propertyId: objectId ?? "",
          tab: INFO_PANEL_TAB_NAME.WORKFLOWS,
        },
      ),
      target: "_blank",
    });
  }

  return <ActionCell actions={actions} />;
};

export const WorkflowsTable = () => {
  const { user, admin } = useContext(AuthContext);
  const { workflowDefinitionId } = useParams<{
    workflowDefinitionId: string;
  }>();

  const {
    data: savedViewsResponse,
    loading: loadingSavedViews,
    refetch: refetchSavedViews,
  } = useGetSavedViewsQuery({
    variables: { table: TABLE_NAMES.WORKFLOWS },
    fetchPolicy: "no-cache",
  });

  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetTableConfigQuery({
      variables: { table: TABLE_NAMES.WORKFLOWS },
      fetchPolicy: "cache-and-network",
    });

  const [
    queryTableData,
    { previousData, data: currentData, networkStatus, loading, error, refetch },
  ] = useGetQueryTableDataLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const refetchTable = () => {
    void refetch();
  };

  const actionsColumn: MinimalColumnDef<
    typeof WORKFLOWS_TABLE_REQUIRED_FIELDS
  > = {
    ...ACTION_COLUMN_DEF_CONSTANTS,
    cell: ({ row }) => {
      const workflow = row.original;
      return <WorkflowActions rowData={workflow} />;
    },
  };

  const tableActions = [<ExportDataButton key={"exportData"} />];

  // Filter saved views to only show ones for the current workflow definition
  const filteredSavedViews = useMemo(() => {
    if (!savedViewsResponse?.account?.savedViews || !workflowDefinitionId) {
      return savedViewsResponse?.account?.savedViews;
    }
    return savedViewsResponse.account.savedViews.filter(view => {
      const workflowDefFilter = view.query.filters?.find(
        (f: { table: string; field: string; value: string }) =>
          f.table === TABLE_NAMES.WORKFLOWS &&
          f.field === "workflowDefinitionId",
      );
      return workflowDefFilter?.value === workflowDefinitionId;
    });
  }, [savedViewsResponse?.account?.savedViews, workflowDefinitionId]);

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    source: { table: TABLE_NAMES.WORKFLOWS },
    defaultSort: [{ id: "Workflows.id", desc: false }],
    savedViews: filteredSavedViews,
    additionalFilters:
      workflowDefinitionId ?
        [
          {
            table: TABLE_NAMES.WORKFLOWS,
            field: "workflowDefinitionId",
            value: workflowDefinitionId,
            operator: FILTER_OPERATORS.EQUALS,
          },
        ]
      : undefined,
  });

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getTableConfig.data || [],
    cellRenderers: makeCustomCellRenderers({ refetchTable }),
  });

  const { id: tableId } = buildLocalTableInfo({
    entityId: user?.id ?? admin?.id,
    pathname: location.pathname,
  });
  const { getLocalTableState, setLocalColumnOrder } =
    useLocalTableDisplayConfig({
      tableId,
      defaultValue: {
        columnOrder: [
          ...queryDescription.fields.map(field => fieldId(field)),
          ACTION_COLUMN_DEF_CONSTANTS.id,
        ],
        columnSizing: {},
        sorting: [],
      },
    });

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.queryTableData.pageInfo.totalPages,
    previousTotalPages: previousData?.queryTableData.pageInfo.totalPages,
  });

  const tanstackColumnDefinitions: Array<ColumnDef<any>> = [
    ...generatedColumnDefinitions,
    actionsColumn,
  ];

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const search = useCallback(
    ({ page }: { page: number }) => {
      const variables: GetQueryTableDataQueryVariables = {
        description: {
          ...queryDescription,
          fields: mergeFieldGroups(
            queryDescription.fields,
            WORKFLOWS_TABLE_REQUIRED_FIELDS,
          ),
        } satisfies QueryDescription,
        page,
      };

      void queryTableData({
        variables,
      });
    },
    [queryDescription, queryTableData],
  );

  useEffect(() => {
    if (queryDescription && !loadingColumnConfig && !loadingSavedViews) {
      search({ page: 1 });
    }
  }, [queryDescription, loadingColumnConfig, loadingSavedViews, search]);

  if (
    loadingColumnConfig ||
    !columnConfigResponse?.getTableConfig.data ||
    loadingSavedViews ||
    !currentView
  ) {
    return null;
  }

  const loadingDetails = {
    loading,
    loadingText: "Loading workflows",
    noDataText: "No workflows found",
  };

  const localTableConfig = getLocalTableState();

  const { columnOrder: localColumnOrder } = localTableConfig;

  const updatedColumnOrder = sanityCheckLocalColumnOrder({
    localColumnOrder,
    defaultColumnIds: queryDescription.fields.map(field => fieldId(field)),
  });

  if (updatedColumnOrder) {
    setLocalColumnOrder(updatedColumnOrder);
  }

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn,
  });

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR",
  );

  return (
    <FullWidthTable
      columns={initialColumns}
      previousData={previousData?.queryTableData.data}
      currentData={data?.queryTableData.data ?? []}
      loadingDetails={loadingDetails}
      manualPaginationConfig={{
        ...manualPaginationConfig,
        pageCount: data?.queryTableData.pageInfo.totalPages ?? 1,
      }}
      initialState={initialTableState}
      actions={tableActions}
      filterable={{
        filterConfiguration: columnConfigResponse.getTableConfig
          .data as Array<Attribute>,
        search,
      }}
      columnSettingProps={{
        columnConfiguration: columnConfigResponse.getTableConfig.data,
        columnDefinitions: tanstackColumnDefinitions,
      }}
      timeoutError={timeoutError}
      queryDescription={queryDescription}
      currentView={currentView}
      updateQueryDescription={updateQueryDescription}
      savedViews={savedViewsResponse?.account?.savedViews}
      refetchSavedViews={refetchSavedViews}
      refetchTable={refetchTable}
      clientSideColumnConfig={columnConfigResponse.getTableConfig.data}
      dataSourceComputationsAffectingQuery={
        data?.queryTableData.dataSourceComputationsAffectingQuery
      }
      invalidQueryConfig={data?.queryTableData.invalidQueryConfig ?? null}
      rowIdField={`${TABLE_NAMES.WORKFLOWS}.id`}
      totalCount={data?.queryTableData.pageInfo.totalCount ?? 0}
      actionBarConfig={columnConfigResponse?.getTableConfig.actionBarConfig}
      hideViewOnMapButton={true}
      filterExclusionMatcher={
        workflowDefinitionId ?
          filterDescription => {
            // Hide the specific workflowDefinitionId filter that matches the URL parameter
            return (
              filterDescription.table === TABLE_NAMES.WORKFLOWS &&
              filterDescription.field === "workflowDefinitionId" &&
              filterDescription.value === workflowDefinitionId
            );
          }
        : undefined
      }
    />
  );
};

export const WorkflowsPage = () => {
  const { workflowDefinitionId } = useParams<{
    workflowDefinitionId: string;
  }>();
  const { account } = useContext(AuthContext);

  const workflowDefinition = account?.workflowDefinitions.find(
    wd => wd.id === workflowDefinitionId,
  );

  const title =
    workflowDefinition?.name ?
      pluralize({
        count: 2,
        options: {
          singular: workflowDefinition.name,
          plural: `${workflowDefinition.name}s`,
        },
      })
    : "Workflows";

  return (
    <Layout title={title}>
      <Switch>
        <Route path={getPath("workflowsTable")} component={WorkflowsTable} />
      </Switch>
    </Layout>
  );
};

export default WorkflowsPage;
